import axios from 'axios';
import { apiUrl } from '../config/api';

// Function to submit loan with contract
// creditAssessmentId is optional (used by the online pre-qualification flow);
// existing call sites that omit it are unaffected.
// options.bureauReport: { file, date }, the client's credit report when no
// pre-qualification holds it (mandatory on every application).
// options.riskReview: the calculator declined the client on affordability and
// the application goes to the assessors for review (risk_review=true),
// unsigned; no rate or dates are sent. The API answers { message, loan, review }.
export const submitLoanWithContract = async (loanData, clientId, contractUrl, bankStatementFile, payslipFile, clientPhotoFile, creditAssessmentId, options = {}) => {
  try {
    const token = localStorage.getItem('token');
    const form = new FormData();
    
    // Append loan data - only include client_id when it's a valid number
    const cid = clientId !== undefined && clientId !== null ? parseInt(clientId, 10) : null;
    if (cid && !isNaN(cid)) {
      form.append('client_id', String(cid));
    }
    const riskReview = options?.riskReview === true;
    form.append('principal_amount', loanData.principal_amount);
    if (!riskReview) form.append('interest_rate', loanData.interest_rate);
    form.append('duration_months', loanData.duration_months);
    if (riskReview) form.append('risk_review', 'true');
    // The API works out the amounts (principal + interest, monthly payment)
    // and, from the deduction day, the dates; the dates are still sent for a
    // loan captured without one.
    if (loanData.deduction_day !== undefined && loanData.deduction_day !== null && String(loanData.deduction_day).trim() !== '') {
      form.append('deduction_day', String(loanData.deduction_day));
    }
    if (!riskReview) {
      form.append('start_date', loanData.start_date);
      form.append('due_date', loanData.due_date);
    }
  // Optional: only include if provided
  if (contractUrl) form.append('contract_url', contractUrl);
    if (loanData.application_type) {
      form.append('application_type', loanData.application_type);
    }
    // Include loan_reason_id if provided
    if (loanData.loan_reason_id !== undefined && loanData.loan_reason_id !== null) {
      const rid = parseInt(loanData.loan_reason_id, 10);
      if (!isNaN(rid)) {
        form.append('loan_reason_id', String(rid));
      }
    }
    
    // Link the loan to a completed credit assessment when provided (online pre-qualification flow)
    if (creditAssessmentId !== undefined && creditAssessmentId !== null && String(creditAssessmentId).trim() !== '') {
      form.append('credit_assessment_id', String(creditAssessmentId));
    }

    // The additional expenses chosen for this loan, as codes. The server
    // prices them from the configured expense types. Absent means "apply the
    // defaults", which is what callers that predate the choice want.
    if (Array.isArray(loanData.additional_expense_codes)) {
      form.append('additional_expenses', JSON.stringify(loanData.additional_expense_codes));
    }

    // Add files if they exist
    if (bankStatementFile) form.append('bankStatement', bankStatementFile);
    if (payslipFile) form.append('payslip', payslipFile);
    if (clientPhotoFile) form.append('clientPhoto', clientPhotoFile);
    if (options?.bureauReport?.file) {
      form.append('bureauReport', options.bureauReport.file);
      form.append('bureau_report_date', options.bureauReport.date || '');
    }
    
    // Submit the loan
    const response = await axios.post(
      apiUrl('/api/loans/add-loans'),
      form,
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
    );
    
    return {
      success: true,
      data: response.data,
      message: 'Loan added successfully'
    };
  } catch (error) {
    console.error('Error submitting loan:', error);
    return {
      success: false,
      message: error.response?.data?.error || error.response?.data?.message || 'Failed to submit loan',
      // The API's code and per-field messages (ACTIVE_LOAN, ABOVE_QUALIFIED_AMOUNT, ...).
      code: error.response?.data?.code || null,
      errors: Array.isArray(error.response?.data?.errors) ? error.response.data.errors : [],
    };
  }
};